import parseLocale from '@/core/i18n/parse-locale';

const FALLBACK_LOCALE = 'de';

/**
 * Locales that keep their country because the language is native to it and the
 * regional format is the intended one. Every other `language-country` pair is
 * formatted language-only: `Intl` has no formatting data of its own for pairs
 * like `en-nl`, and engines disagree on the fallback (Node renders `€29.50`,
 * Safari `€ 29,50`), which breaks hydration when a price is rendered both on
 * the server and on the client.
 */
const REGIONAL_PRICE_LOCALES = new Set([
  'de-at',
  'de-ch',
  'fr-ch',
  'it-ch',
  'pt-pt',
]);

const resolveFormattingLocale = (locale: string): string => {
  const { language, country } = parseLocale(locale.toLowerCase());
  if (!language) return FALLBACK_LOCALE;

  const regionalLocale = `${language}-${country}`;
  return country && REGIONAL_PRICE_LOCALES.has(regionalLocale)
    ? regionalLocale
    : language;
};

export type FormatPriceOptions = {
  /** Always show two fraction digits (e.g. €10.00), including for whole amounts. */
  alwaysShowFractionDigits?: boolean;
};

const formatPrice = (
  price: number | null | undefined,
  currency: string | undefined,
  locale: string = FALLBACK_LOCALE,
  options?: FormatPriceOptions,
): string => {
  if (!price && price !== 0) return '0';
  if (!currency) return '€';

  const fractionDigits =
    options?.alwaysShowFractionDigits || price % 1 !== 0 ? 2 : 0;

  return new Intl.NumberFormat(resolveFormattingLocale(locale), {
    style: 'currency',
    currency,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(price);
};

export default formatPrice;
